import { useLocation, useNavigate } from 'react-router-dom';
import { Droplet, Plus } from 'lucide-react';
import { useFarmFromUrl } from '@/shared/hooks/useUrlSync';
import { useFeaturePageState } from '@/shared/hooks/useFeaturePageState';
import { TabNavigation } from '@/features/shared/components/TabNavigation';
import { PondFilter } from '@/features/shared/components/PondFilter';
import type { Tab } from '@/features/shared/components/TabNavigation';
import type { PondOption } from '@/features/shared/components/PondFilter';
import { PageLoading } from '@/shared/components/ui/LoadingSpinner';
import { FarmRequiredGuard } from '@/shared/components/FarmRequiredGuard';
import { WaterQualityRecordsTab } from '../components/WaterQualityRecordsTab';
import { WaterQualityTrendsTab } from '../components/WaterQualityTrendsTab';
import { WaterQualityExportButton } from '../components/WaterQualityExportButton';
import { Button } from '@/shared/components/ui/Button';
import { buildRoute } from '@/shared/lib/constants';

type TabId = 'records' | 'trends';

// Tab configuration for water quality feature
const WATER_QUALITY_TABS = [
  { id: 'records', path: '' },
  { id: 'trends', path: '/trends' },
];

export function WaterQualityPageNew() {
  const { farmId, farm, isLoading: isFarmLoading, isError: isFarmError, error: farmError } = useFarmFromUrl();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine active tab from URL path
  const activeTab: TabId = location.pathname.endsWith('/trends') ? 'trends' : 'records';

  // Use shared hook for pond selection and tab navigation
  // This handles: pond fetching, URL sync, localStorage restoration, and tab navigation
  const {
    selectedPondId,
    setSelectedPondId,
    navigateToTab,
    ponds,
    isPondsLoading,
  } = useFeaturePageState({
    farmId,
    feature: 'water-quality',
    tabs: WATER_QUALITY_TABS,
    currentTab: activeTab,
  });

  // Check if ponds query failed (ponds will be empty array on error)
  const isPondsError = !isPondsLoading && farmId && ponds.length === 0;

  // Show loading state - but only if actually loading, not if query is disabled or errored
  if (isFarmLoading || (isPondsLoading && !isPondsError)) {
    return <PageLoading message="Loading..." />;
  }

  // ponds from useFeaturePageState is already an empty array if loading failed

  const tabs: Tab[] = [
    {
      id: 'records',
      label: 'Records',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'trends',
      label: 'Trends',
      icon: (
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
  ];

  const pondOptions: PondOption[] = ponds.map((pond) => ({
    id: pond.id,
    name: pond.name,
    // TODO: Add record count when available from backend
  }));

  if (!farm && farmId && !isFarmLoading) {
    const errorWithResponse = farmError as { response?: { status?: number } } | null;
    const errorStatus = errorWithResponse?.response?.status;
    let errorTitle = 'Farm not found';
    let errorMessage = "The farm you're looking for doesn't exist or you don't have access to it.";

    if (isFarmError) {
      if (errorStatus === 404) {
        errorTitle = 'Farm not found';
        errorMessage = "This farm doesn't exist. It may have been deleted or the link is incorrect.";
      } else if (errorStatus === 403) {
        errorTitle = 'Access denied';
        errorMessage = "You don't have permission to view this farm. Please contact the farm owner for access.";
      } else if (errorStatus && errorStatus >= 500) {
        errorTitle = 'Server error';
        errorMessage = 'Unable to load farm data. Please try again later.';
      }
    }

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-red-100 border border-red-200/50 mb-4">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h3 className="font-roboto text-lg font-semibold text-gray-900">{errorTitle}</h3>
          <p className="font-roboto mt-2 text-sm text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  // Show farm selection guard if no farmId
  if (!farmId) {
    return (
      <FarmRequiredGuard farmId={farmId} isLoading={isFarmLoading}>
        {null}
      </FarmRequiredGuard>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ponds Loading Error Banner */}
      {isPondsError && (
        <div className="rounded-lg bg-amber-50 border border-amber-200/50 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="font-roboto text-sm font-medium text-amber-900">
                Unable to load ponds
              </p>
              <p className="font-roboto text-sm text-amber-700 mt-0.5">
                There was an error loading ponds. Some features may be limited.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <span>{farm?.name}</span>
          <span>›</span>
          <span className="text-gray-900 font-medium">Water Quality</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200/50">
              <Droplet className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="font-roboto text-2xl font-bold text-gray-900">Water Quality</h1>
              <p className="font-roboto mt-1 text-sm text-gray-600">
                {selectedPondId && activeTab === 'records'
                  ? `Water quality records for ${ponds.find((p) => p.id === selectedPondId)?.name ?? 'selected pond'}`
                  : activeTab === 'trends'
                  ? 'Compare water quality trends across ponds'
                  : 'Monitor water parameters across the farm'}
              </p>
            </div>
          </div>
          {activeTab === 'records' && (
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                onClick={() => navigate(buildRoute.waterQualityNew(farmId!, selectedPondId))}
                disabled={!selectedPondId}
                title={!selectedPondId ? "Select a pond first" : undefined}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Test
              </Button>
              <WaterQualityExportButton
                farmId={farmId}
                pondId={selectedPondId}
                pondName={ponds.find((p) => p.id === selectedPondId)?.name}
                disabled={!selectedPondId}
              />
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onChange={navigateToTab} />

      {/* Pond Filter (only show on Records tab) */}
      {activeTab === 'records' && ponds.length > 0 && (
        <div className="max-w-xs">
          <PondFilter
            ponds={pondOptions}
            selectedPondId={selectedPondId}
            onChange={setSelectedPondId}
            label="Filter by Pond"
          />
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'records' ? (
        <WaterQualityRecordsTab
          farmId={farmId!}
          selectedPondId={selectedPondId}
          onRecordClick={(record) => {
            if (record.pondId) {
              navigate(buildRoute.waterQualityTest(farmId!, record.pondId, record.id));
            }
          }}
        />
      ) : (
        <WaterQualityTrendsTab farmId={farmId!} ponds={ponds} />
      )}
    </div>
  );
}
